import { IJournalEntryDialogProps } from '../components/sidebar/JournalEntryDialog';
import { DrillDownAction, DrillDownActionType, IEnterAmountsOptionalProps } from '../components/sidebar/ReportDrillDownActions';
import {
    AccountAmountWhitePaperRow,
    getAccountAmountDataSource,
    getAccountAmountSummaryByEphemeralKey,
    ITrackedExtractionDetailsAccountSummary,
    ITrackedUpdateTime,
    IWhitePaperRowTrace,
    searchAndKeyChildTraces,
} from '../components/sidebar/ReportDrillDownUtils';
import { AllowNull } from '../data-types/AllowUndefinedAndNull';
import {
    ExtractionDetailsAdjustmentEntry,
    ExtractionDetailsAdjustmentInfo,
    ExtractionDetailsBookEntry,
    ExtractionDetailsBookInfo,
    ExtractionDetailsRequest,
    IWhitePaperReportChanges,
    ProblemDetails,
    Report,
    SaveAmountResponse,
    SaveAmountsResponse,
} from '../model';
import { addDrillDownAmountCellToChangeTrackingData } from '../utils/ChangeTrackingDataUtils';
import { deFormatNumber } from '../utils/NumberUtils';

export interface IRowChangeSummary {
    changed: boolean;
    newValue?: string;
}

export interface IExportButtonState {
    isProcessing: boolean;
    onClick: () => void;
}

export interface IErrorMessage {
    message: string;
    needsTranslation: boolean;
    translationArgs?: any;
}

export interface IDrillDownState {
    openAccountSummaryItems: Iterable<string>;
    openTraceCalculationItems: Map<string, IWhitePaperRowTrace[]>;
    journalEntryDialogProps: AllowNull<IJournalEntryDialogProps>;
    activeTabIndex: number;
    exportButtonStateMap: Map<number, IExportButtonState>;
    showConfirmationDialog: boolean;
    currentAccountAmountSummaries: ITrackedExtractionDetailsAccountSummary[];
    isInitializing: boolean;
    errorMessages: IErrorMessage[];
    isServerError: boolean;
    rows: AccountAmountWhitePaperRow[] | null;

    accountAmountGridHasData: boolean;
    amountsChangedOnServer: boolean;
    isFetchingOrSavingData: boolean;
    changeTrackingData: IWhitePaperReportChanges;
    showConfirmationDialogOnExit: boolean;

    // if not null, the enter amounts dialog will appear. if null, the dialog will not appear
    enterAmountsDialogProps: AllowNull<IEnterAmountsOptionalProps>;
    // optional row key saved when EnterAmounts dialog is opened (opt-in via action)
    enterAmountsDialogSummaryKey?: string | null;
    updatedChangeTrackingData: IWhitePaperReportChanges;
    updatedChangeTrackingDataMap?: Map<string, IRowChangeSummary>;

    definingRows?: IWhitePaperRowTrace[];
    rowsImpacted?: IWhitePaperRowTrace[];
    totalRow?: IWhitePaperRowTrace;
}

const getMergedAccountSummaries = (
    rows: AccountAmountWhitePaperRow[],
    currentAccountAmountSummaries: ITrackedExtractionDetailsAccountSummary[]
) => {
    const mergedAccountSummaries = [] as ITrackedExtractionDetailsAccountSummary[];
    rows?.forEach((row: AccountAmountWhitePaperRow) => {
        const summary = row.dataObject as ITrackedExtractionDetailsAccountSummary;
        const dirtySummary = getAccountAmountSummaryByEphemeralKey(
            summary.accountNumber!,
            summary.ephemeralKey!,
            currentAccountAmountSummaries
        );
        const summaryForAccount = dirtySummary ? dirtySummary : summary;
        mergedAccountSummaries.push({ ...summaryForAccount });
    });
    return mergedAccountSummaries;
};

export type DrillDownStateReducer = (state: IDrillDownState, action: DrillDownAction) => IDrillDownState;

export function drillDownStateReducer(state: IDrillDownState, action: DrillDownAction): IDrillDownState {
    try {
        switch (action.type) {
            case DrillDownActionType.ChangeActiveTabIndex:
                return { ...state, activeTabIndex: action.newActiveTab ?? 0 };
            case DrillDownActionType.StartProcessingExport: {
                const exportButtonStateMap = new Map(state.exportButtonStateMap);
                const exportButtonState = exportButtonStateMap.get(action.eventTargetTabIndex!);
                if (exportButtonState) {
                    exportButtonState.isProcessing = true;
                    exportButtonStateMap.set(action.eventTargetTabIndex!, exportButtonState);
                }
                return { ...state, exportButtonStateMap, errorMessages: [] };
            }
            case DrillDownActionType.StopProcessingExport: {
                const exportButtonStateMapStop = new Map(state.exportButtonStateMap);
                const exportButtonStateStop = exportButtonStateMapStop.get(action.eventTargetTabIndex!);
                if (exportButtonStateStop) {
                    exportButtonStateStop.isProcessing = false;
                    exportButtonStateMapStop.set(action.eventTargetTabIndex!, exportButtonStateStop);
                }
                return { ...state, exportButtonStateMap: exportButtonStateMapStop };
            }
            case DrillDownActionType.StartProcessingSaveAmounts:
                return {
                    ...state,
                    isInitializing: false,
                    isFetchingOrSavingData: true,
                    isServerError: false,
                    errorMessages: [],
                    showConfirmationDialog: false,
                    showConfirmationDialogOnExit: false,
                };
            case DrillDownActionType.SaveAmountsSuccess: {
                const mergedSummaries = getMergedAccountSummaries(state.rows ?? [], state.currentAccountAmountSummaries);
                let newRows: AccountAmountWhitePaperRow[] | null = null;

                if (state.rows && mergedSummaries && mergedSummaries.length > 0) {
                    const mergedByKey = new Map<number, ITrackedExtractionDetailsAccountSummary>();
                    for (const ms of mergedSummaries) {
                        if (ms.ephemeralKey !== undefined) {
                            mergedByKey.set(ms.ephemeralKey, ms);
                        }
                    }

                    newRows = state.rows.map((r) => {
                        const ephemeralKey = r.summaryEphemeralKey;
                        const updatedSummary = mergedByKey.get(ephemeralKey);
                        if (updatedSummary) {
                            // create a new AccountAmountWhitePaperRow with updated dataObject while preserving children/structure
                            return new AccountAmountWhitePaperRow(
                                updatedSummary,
                                'AccountSummary',
                                (r as any)._children,
                                null,
                                r.recalcNeeded
                            );
                        }
                        return r;
                    });
                } else {
                    newRows = state.rows;
                }

                return {
                    ...state,
                    rows: newRows,
                    currentAccountAmountSummaries: [],
                    amountsChangedOnServer: true,
                    changeTrackingData: action.updatedChangeTrackingData!,
                    updatedChangeTrackingData: {},
                };
            }
            case DrillDownActionType.SaveAmountsFailure:
                // what data comes back if there's no errors?
                if (action?.error?.response?.status === 500) {
                    const problemDetails: ProblemDetails = action?.error?.response?.data as ProblemDetails;
                    return {
                        ...state,
                        isServerError: true,
                        errorMessages: [{ message: `${problemDetails?.title ?? ''} - ${problemDetails?.traceId ?? ''}` } as IErrorMessage],
                    };
                } else {
                    // orval doesn't seem to handle the case where the api can return one of two distinct model types depending on the error code.
                    // so we need to cast the response data as unknown first as an intermediary step
                    const response: SaveAmountsResponse = action?.error?.response?.data as unknown as SaveAmountsResponse;
                    const errorMessagesForAmounts: IErrorMessage[] = response?.amountResponses?.map(mapSaveAmountsError()) ?? [];
                    const requestLevelErrorMessages: IErrorMessage[] =
                        response?.errors?.map((error) => {
                            return {
                                message: error,
                                needsTranslation: false,
                            } as IErrorMessage;
                        }) ?? [];
                    const errorMessages: IErrorMessage[] = requestLevelErrorMessages.concat(errorMessagesForAmounts);
                    return { ...state, isServerError: false, errorMessages: errorMessages };
                }
            case DrillDownActionType.SaveAmountsComplete:
                return { ...state, isFetchingOrSavingData: false };
            case DrillDownActionType.AmountEdited: {
                const item: AccountAmountWhitePaperRow = action.item;
                let updatedAccountAmountSummaries: ITrackedExtractionDetailsAccountSummary[] | undefined;
                let updatedChangeTrackingData: IWhitePaperReportChanges | undefined;

                switch (item!.dataObjectType) {
                    case 'AdjustmentEntry':
                    case 'AdjustmentSummary':
                        [updatedAccountAmountSummaries, updatedChangeTrackingData] = onAdjustmentEntryChanged(
                            action.event,
                            action.report,
                            action.rowNumber,
                            action.columnId,
                            item,
                            state.currentAccountAmountSummaries,
                            state.rows,
                            state.updatedChangeTrackingData
                        );

                        break;

                    default:
                        break;
                }
                const newMap = new Map<string, IRowChangeSummary>(state.updatedChangeTrackingDataMap ?? []);
                const rowKey = item.uiKey;
                const newValue = action?.event?.target?.innerHTML ? deFormatNumber(action.event.target.innerHTML) : undefined;
                if (rowKey) {
                    newMap.set(rowKey, { changed: true, newValue });
                }

                return {
                    ...state,
                    currentAccountAmountSummaries: updatedAccountAmountSummaries ?? state.currentAccountAmountSummaries,
                    updatedChangeTrackingData: updatedChangeTrackingData ?? state.updatedChangeTrackingData,
                    updatedChangeTrackingDataMap: newMap,
                    showConfirmationDialogOnExit: true,
                };
            }

            case DrillDownActionType.ExportFailure: {
                const drillDownExportType: string = action.eventTargetTabIndex === 0 ? 'Account/Amount' : 'Trace Calculation';
                const exportButtonStateMapFailure = new Map(state.exportButtonStateMap);
                const exportButtonStateFailure = exportButtonStateMapFailure.get(action.eventTargetTabIndex!);
                if (exportButtonStateFailure) {
                    exportButtonStateFailure.isProcessing = false;
                    exportButtonStateMapFailure.set(action.eventTargetTabIndex!, exportButtonStateFailure);
                }
                return {
                    ...state,
                    exportButtonStateMap: exportButtonStateMapFailure,
                    errorMessages: [{ message: 'exportError', needsTranslation: true, translationArgs: { drillDownExportType } }],
                };
            }
            case DrillDownActionType.RequestCellAuditStart: {
                const requests: ExtractionDetailsRequest[] | undefined = action.extractionDetailsRequests;

                // Calculated row references only rows which have been filtered from the report.
                // This is a valid scenario, but we don't need to make a request for this row.
                const initialRows: AccountAmountWhitePaperRow[] | null = requests?.length === 0 ? [] : null;
                return {
                    ...state,
                    rows: initialRows,
                    isInitializing: false,
                    isFetchingOrSavingData: true,
                    isServerError: false,
                    errorMessages: [],
                };
            }
            case DrillDownActionType.RequestCellAuditEnd:
                return { ...state, isFetchingOrSavingData: false };
            case DrillDownActionType.RequestCellAuditFailure: {
                const problemDetails: ProblemDetails = action.error?.response?.data as ProblemDetails;
                const errorMessages: IErrorMessage[] = problemDetails
                    ? [{ message: `${problemDetails?.title ?? ''} - ${problemDetails?.traceId ?? ''}` } as IErrorMessage]
                    : // this handles network errors (error.code === "ERR_NETWORK") as well as HTTP 401 responses.
                      // It would be more consistent if HTTP 401 responses were returned as ProblemDetails too,
                      // but that would probably require some kind of middleware change in BFF which is riskier than just doing this
                      [{ message: 'networkError', needsTranslation: true } as IErrorMessage];
                return { ...state, isServerError: true, errorMessages: errorMessages };
            }
            case DrillDownActionType.RequestCellAuditSuccess: {
                const initialSummaries = action.initialAccountAmountSummaries ?? null;
                const newMap = new Map<string, IRowChangeSummary>(state.updatedChangeTrackingDataMap ?? []);
                let rows: AccountAmountWhitePaperRow[] | null = null;
                const reportToUse: Report | undefined = action.report;
                const rowNumberToUse: number | undefined = action.rowNumber;
                const columnIdToUse: number | undefined = action.columnId;
                if (initialSummaries && rowNumberToUse !== undefined && columnIdToUse !== undefined && reportToUse) {
                    rows = getAccountAmountDataSource(
                        initialSummaries,
                        rowNumberToUse,
                        columnIdToUse,
                        reportToUse,
                        state.changeTrackingData
                    );
                    // populate map for recalcNeeded flags on adjustment entries/summaries and account-level recalc
                    for (const row of rows) {
                        if (row.recalcNeeded) {
                            const k = row.uiKey;
                            if (k) {
                                newMap.set(k, { changed: true });
                            }
                        }
                        const adjSummaries = row.adjustmentSummaries ?? [];
                        for (const adj of adjSummaries) {
                            if (adj.recalcNeeded) {
                                const k = adj.uiKey;
                                if (k) {
                                    newMap.set(k, { changed: true });
                                }
                            }
                            const entries = adj.adjustmentEntries ?? [];
                            for (const e of entries) {
                                if (e.recalcNeeded) {
                                    const k = e.uiKey;
                                    if (k) {
                                        newMap.set(k, { changed: true });
                                    }
                                }
                            }
                        }
                    }
                }

                return {
                    ...state,
                    rows,
                    updatedChangeTrackingDataMap: newMap,
                };
            }
            case DrillDownActionType.ConfirmationDialogOpened:
                return { ...state, showConfirmationDialog: true };
            case DrillDownActionType.ConfirmationDialogClosed:
                return { ...state, showConfirmationDialog: false };
            case DrillDownActionType.EnterAmountsDialogOpened:
                return {
                    ...state,
                    enterAmountsDialogProps: action.enterAmountsDialogProps,
                    enterAmountsDialogSummaryKey: action.summaryKey ?? null,
                };

            case DrillDownActionType.EnterAmountsDialogClosed: {
                const amountsChangedOnServer: boolean = !!action.amountsChangedOnServer;

                // If amounts changed on server and we have a stored row key, mark that specific row dirty
                if (amountsChangedOnServer) {
                    const rowKey = state.enterAmountsDialogSummaryKey;
                    if (!rowKey) {
                        console.error('Missing stored row key when EnterAmountsDialogClosed indicates server changes');
                    } else {
                        const newMap = new Map<string, IRowChangeSummary>(state.updatedChangeTrackingDataMap ?? []);
                        newMap.set(rowKey, { changed: true });

                        return {
                            ...state,
                            changeTrackingData: action.updatedChangeTrackingData!,
                            enterAmountsDialogProps: null,
                            enterAmountsDialogSummaryKey: null,
                            updatedChangeTrackingDataMap: newMap,
                            amountsChangedOnServer: state.amountsChangedOnServer || amountsChangedOnServer,
                        };
                    }
                }

                return {
                    ...state,
                    changeTrackingData: action.updatedChangeTrackingData!,
                    enterAmountsDialogProps: null,
                    enterAmountsDialogSummaryKey: null,
                    amountsChangedOnServer: state.amountsChangedOnServer || amountsChangedOnServer,
                };
            }
            case DrillDownActionType.JournalEntryDialogOpened:
                return { ...state, journalEntryDialogProps: action.journalEntryDialogProps };
            case DrillDownActionType.JournalEntryDialogClosed:
                return { ...state, journalEntryDialogProps: null };
            case DrillDownActionType.AccountAmountExpandChanged:
                return { ...state, openAccountSummaryItems: Array.from(action.openIds).map((id) => id.toString()) };
            case DrillDownActionType.TraceCalculationExpandChanged: {
                // If no uiKey provided, this is a reset action (collapse all)
                if (!action.uiKey) {
                    return { ...state, openTraceCalculationItems: new Map() };
                }

                const newExpandedChildren = new Map(state.openTraceCalculationItems);

                if (action.isOpening) {
                    // Special handling for section headers (definingRows, rowsImpacted)
                    if (action.uiKey === 'definingRows' || action.uiKey === 'rowsImpacted') {
                        // Call searchAndKeyChildTraces to ensure unique object instances with proper child keying
                        const keyedChildren = searchAndKeyChildTraces(
                            action.uiKey,
                            state.definingRows ?? [],
                            state.rowsImpacted ?? [],
                            newExpandedChildren
                        );

                        if (keyedChildren) {
                            newExpandedChildren.set(action.uiKey, keyedChildren);
                        } else {
                            // Fallback: if searchAndKeyChildTraces doesn't find the section, create synthetic keying
                            const tracesForSection = action.uiKey === 'definingRows' ? state.definingRows : state.rowsImpacted;
                            if (tracesForSection) {
                                const rowNumberIndices: Map<number, number> = new Map();
                                const tracesWithSiblingIndices = tracesForSection.map((trace) => {
                                    const rowNumber: number = trace.rowNumber ?? 0;
                                    const siblingIndex: number = rowNumberIndices.get(rowNumber) ?? 0;
                                    rowNumberIndices.set(rowNumber, siblingIndex + 1);

                                    const updatedTrace = { ...trace };
                                    const pathPart = `${rowNumber}`;
                                    updatedTrace.uiKey = `${action.uiKey}|${pathPart}||${siblingIndex}`;
                                    return updatedTrace;
                                });
                                newExpandedChildren.set(action.uiKey, tracesWithSiblingIndices);
                            } else {
                                newExpandedChildren.set(action.uiKey, []);
                            }
                        }
                    } else if (state.definingRows && state.rowsImpacted) {
                        // Node is being expanded: search and set children
                        const keyedChildren = searchAndKeyChildTraces(
                            action.uiKey,
                            state.definingRows,
                            state.rowsImpacted,
                            newExpandedChildren
                        );

                        if (keyedChildren) {
                            newExpandedChildren.set(action.uiKey, keyedChildren);
                        }
                    }
                } else {
                    // Node is being collapsed: remove from map
                    newExpandedChildren.delete(action.uiKey);
                }

                return { ...state, openTraceCalculationItems: newExpandedChildren };
            }
            default:
                throw new Error('unsupported action type');
        }
    } catch (e: any) {
        const error: Error = e as Error;
        console.error(error.stack);
        return {
            ...state,
            errorMessages: [{ message: 'genericDrillDownError', needsTranslation: true, translationArgs: { errorMessage: error.message } }],
        };
    }
}

const extractionDetailEntriesAreEqual = (
    left: ExtractionDetailsBookEntry | ExtractionDetailsAdjustmentEntry,
    right: ExtractionDetailsBookEntry | ExtractionDetailsAdjustmentEntry
) => {
    return (
        left.corporation === right.corporation &&
        left.case === right.case &&
        left.jurisdiction === right.jurisdiction &&
        left.location === right.location &&
        left.year === right.year
    );
};

const getAccountSummaryForAmountUpdate = (
    accountNumber: string,
    ephemeralKey: number,
    currentAccountAmountSummaries: ITrackedExtractionDetailsAccountSummary[],
    rows: AccountAmountWhitePaperRow[]
): [ITrackedExtractionDetailsAccountSummary | undefined, boolean] => {
    let accountSummary: ITrackedExtractionDetailsAccountSummary | undefined = getAccountAmountSummaryByEphemeralKey(
        accountNumber,
        ephemeralKey,
        currentAccountAmountSummaries
    );

    const isAccountSummaryDirty: boolean = !!accountSummary;

    if (!isAccountSummaryDirty) {
        // find in rows
        const matchingRow = (rows ?? []).find((r) => r.summaryEphemeralKey === ephemeralKey && r.account === accountNumber);
        if (matchingRow) {
            accountSummary = matchingRow.dataObject as ITrackedExtractionDetailsAccountSummary;
        }
    }

    return [accountSummary, isAccountSummaryDirty];
};

const onAdjustmentEntryChanged = (
    event: any,
    report: Report,
    rowNumber: number,
    columnId: number,
    item: AccountAmountWhitePaperRow,
    currentAccountAmountSummaries: ITrackedExtractionDetailsAccountSummary[],
    rows: AccountAmountWhitePaperRow[] | null,
    currentChangeTrackingData: IWhitePaperReportChanges
): [ITrackedExtractionDetailsAccountSummary[] | undefined, IWhitePaperReportChanges | undefined] => {
    const accountAmountsSummaries: ITrackedExtractionDetailsAccountSummary[] | undefined = getUpdatedAccountSummaries(
        event,
        item,
        currentAccountAmountSummaries,
        rows ?? []
    );
    const changeTrackingData: IWhitePaperReportChanges | undefined = addDrillDownAmountCellToChangeTrackingData(
        report,
        rowNumber,
        columnId,
        item,
        currentChangeTrackingData
    );

    return [accountAmountsSummaries, changeTrackingData];
};

const addUpdatedAmountToCurrentAccountAmountSummaries = (
    newAmount: string,
    updatedItem:
        | ExtractionDetailsAdjustmentEntry
        | ExtractionDetailsAdjustmentInfo
        | ExtractionDetailsBookEntry
        | ExtractionDetailsBookInfo
        | undefined,
    updatedItemAccountSummary: ITrackedExtractionDetailsAccountSummary | undefined,
    isAccountSummaryAlreadyDirty: boolean,
    currentAccountAmountSummaries: ITrackedExtractionDetailsAccountSummary[] | null
): ITrackedExtractionDetailsAccountSummary[] | undefined => {
    if (updatedItem!.reportAmount === newAmount) {
        return;
    }
    const finalUpdates: ITrackedExtractionDetailsAccountSummary[] = [...currentAccountAmountSummaries!];

    if (!isAccountSummaryAlreadyDirty && updatedItemAccountSummary) {
        // TODO:  This will likely need to be revised if/when revert of changed values is implemented as it will overwrite original values
        //        in referenced data objects.
        finalUpdates.push({ ...updatedItemAccountSummary });
    }

    // TODO: Currently it is assumed that we will find the row to update.
    //       Checking and handling for when no existing row is found will need to be added if/when functionality is implemented where
    //       a row may not exist (I.E. adding a new row or deleting an existing row)
    updatedItem!.reportAmount = newAmount;
    return finalUpdates;
};

function mapSaveAmountsError(): (value: SaveAmountResponse, index: number, array: SaveAmountResponse[]) => IErrorMessage {
    return (response) => {
        return {
            message: response.adjustmentCode ? 'saveAmountErrorWithAdjustmentCode' : 'saveAmountError',
            needsTranslation: true,
            translationArgs: {
                account: response.accountCode,
                entity: response.entity,
                adjustmentCode: response.adjustmentCode,
                joinedMessages: response.errors?.join(' ') ?? '',
            },
        };
    };
}

const getUpdatedAccountSummaries = (
    event: any,
    item: AccountAmountWhitePaperRow,
    currentAccountAmountSummaries: ITrackedExtractionDetailsAccountSummary[],
    rows: AccountAmountWhitePaperRow[]
): ITrackedExtractionDetailsAccountSummary[] | undefined => {
    const isItemAdjustmentEntry: boolean = item.dataObjectType === 'AdjustmentEntry'; // If false, then item is AdjustmentSummary
    const accountNumber: string = item.account!;
    const ephemeralKey: number = item.summaryEphemeralKey;
    const adjustmentCode: string = isItemAdjustmentEntry ? item.parent!.adjustmentCode! : item.adjustmentCode!;
    const [accountSummary, isAccountSummaryDirty] = getAccountSummaryForAmountUpdate(
        accountNumber,
        ephemeralKey,
        currentAccountAmountSummaries,
        rows
    );

    const adjustmentInfo: ExtractionDetailsAdjustmentInfo | undefined = accountSummary!.adjustmentInfo!.find(
        (adjustmentInfo) => adjustmentInfo.adjustmentCode === adjustmentCode
    );

    let adjustmentItemToUpdate: ExtractionDetailsAdjustmentEntry | ExtractionDetailsAdjustmentInfo | undefined;

    if (isItemAdjustmentEntry) {
        adjustmentItemToUpdate = adjustmentInfo!.entries!.find((adjustmentEntry) =>
            extractionDetailEntriesAreEqual(adjustmentEntry, item.dataObject)
        );
    } else {
        adjustmentItemToUpdate = adjustmentInfo;
    }

    (adjustmentItemToUpdate as ITrackedUpdateTime).updateTimestamp = Date.now();

    return addUpdatedAmountToCurrentAccountAmountSummaries(
        deFormatNumber(event.target.innerHTML),
        adjustmentItemToUpdate,
        accountSummary,
        isAccountSummaryDirty,
        currentAccountAmountSummaries
    );
};
